// @flow
/* eslint-disable ta-code-conventions/has-matching-test */
// See https://jira.tamg.io/browse/WP-2462
import * as React from 'react';
import useAutoFocus, { Focus } from './useAutoFocus';

type Props = {|
  /** If true, doe not focus the immediate child on mount */
  disableFocusOnMount?: boolean,
  /** If true, will not serve as an anchor point to restore focus to on unmount of a descendant focus */
  disableFocusRestorePoint?: boolean,
  /** If true, will not restore focus to a previous point */
  disableRestoreFocus?: boolean,
  /** The content node to render */
  children: React.Node,
|};

/** A convenient component which attaches the ref of a useFocus hook for you on the most immediate child */
const AutoFocus = ({
  disableFocusOnMount,
  disableFocusRestorePoint,
  disableRestoreFocus,
  children,
}: Props) => {
  const [container, focus] = useAutoFocus({ disableFocusOnMount, disableRestoreFocus });
  const [child, ...rest] = React.Children.toArray(children);
  if (
    child === null ||
    typeof child === 'number' ||
    typeof child === 'string' ||
    typeof child === 'function'
  ) {
    console.error(`First child must be a react element! Found: ${child}`);
  }
  const refInjectedChildren = [
    React.cloneElement(child, {
      ref: node => {
        container.current = node;
        // callback style refs
        if (typeof child.ref === 'function') {
          child.ref(node);
        } else if (typeof child.ref === 'object' && child.ref) {
          // object style refs
          child.ref.current = node;
        }
      },
    }),
    ...rest,
  ];

  if (disableFocusRestorePoint) {
    return refInjectedChildren;
  }
  return <Focus.Provider value={focus}>{refInjectedChildren}</Focus.Provider>;
};

export default AutoFocus;
